(() => {
  const ROOT_SELECTOR = 'product-info.tt-product-v2';

  const text = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  function buildTableMatrix(table) {
    const rows = Array.from(table.rows || []);
    const matrix = [];

    rows.forEach((row, rowIndex) => {
      matrix[rowIndex] ||= [];
      let columnIndex = 0;

      Array.from(row.cells || []).forEach((cell) => {
        while (matrix[rowIndex][columnIndex] !== undefined) columnIndex += 1;

        const rowSpan = Math.max(1, Number(cell.rowSpan) || 1);
        const colSpan = Math.max(1, Number(cell.colSpan) || 1);
        const value = text(cell.textContent);

        for (let r = 0; r < rowSpan; r += 1) {
          matrix[rowIndex + r] ||= [];
          for (let c = 0; c < colSpan; c += 1) {
            matrix[rowIndex + r][columnIndex + c] = value;
          }
        }

        columnIndex += colSpan;
      });
    });

    return matrix;
  }

  function normaliseUnit(value) {
    const unit = text(value).toLowerCase().replace(/\./g, '');
    if (unit === 'cm' || unit.includes('centimet')) return 'cm';
    if (unit === 'in' || unit === 'inch' || unit === 'inches') return 'in';
    return '';
  }

  function parseSizeTable(table) {
    const matrix = buildTableMatrix(table);
    if (matrix.length < 2) return null;

    let unitRowIndex = -1;
    let bestUnitCount = 0;

    matrix.forEach((row, index) => {
      const count = row.filter((cell) => normaliseUnit(cell)).length;
      if (count > bestUnitCount) {
        bestUnitCount = count;
        unitRowIndex = index;
      }
    });

    if (bestUnitCount < 2) unitRowIndex = -1;

    const sizeRowIndex = unitRowIndex > 0 ? unitRowIndex - 1 : 0;
    const sizeRow = matrix[sizeRowIndex] || [];
    const unitRow = unitRowIndex >= 0 ? (matrix[unitRowIndex] || []) : [];
    const dataStart = unitRowIndex >= 0 ? unitRowIndex + 1 : sizeRowIndex + 1;

    const sizes = [];
    const columns = [];
    const firstDataRow = matrix[dataStart] || [];
    const columnCount = Math.max(sizeRow.length, firstDataRow.length);

    for (let col = 1; col < columnCount; col += 1) {
      const size = text(sizeRow[col]);
      if (!size) continue;

      const key = size.toLowerCase();
      if (!sizes.some((item) => item.toLowerCase() === key)) sizes.push(size);
      columns.push({
        index: col,
        size,
        unit: normaliseUnit(unitRow[col])
      });
    }

    if (sizes.length < 2 || columns.length < 2) return null;

    const units = [];
    columns.forEach(({ unit }) => {
      if (unit && !units.includes(unit)) units.push(unit);
    });

    const measurements = [];
    for (let rowIndex = dataStart; rowIndex < matrix.length; rowIndex += 1) {
      const row = matrix[rowIndex] || [];
      const label = text(row[0]);
      if (!label) continue;

      const values = {};
      columns.forEach(({ index, size, unit }) => {
        const value = text(row[index]);
        if (!value) return;
        values[size] ||= {};
        values[size][unit || 'value'] = value;
      });

      if (Object.keys(values).length) measurements.push({ label, values });
    }

    if (!measurements.length) return null;
    return { sizes, units, measurements };
  }

  function tableScore(table) {
    return Array.from(table.rows || []).reduce((score, row) => score + row.cells.length, 0);
  }

  function getLikelySizeTable(source) {
    const tables = Array.from(source.querySelectorAll('table'));
    if (!tables.length) return null;
    return tables.sort((a, b) => tableScore(b) - tableScore(a))[0];
  }

  function removeNearbySizeHeading(table) {
    const previous = table.previousElementSibling;
    if (!previous) return;
    const value = text(previous.textContent).toLowerCase();
    if (value.length < 50 && /(size|measurement|dimensions)/.test(value)) previous.remove();
  }

  function extractCare(source, root) {
    const careAccordion = root.querySelector('[data-tt-care-accordion]');
    const careContent = root.querySelector('[data-tt-care-content]');
    if (!careAccordion || !careContent) return;

    const directChildren = Array.from(source.children);
    const careNodes = directChildren.filter((node) => {
      const value = text(node.textContent).toLowerCase();
      return /^(care instructions?|care:)\b/.test(value);
    });

    if (!careNodes.length) return;

    careNodes.forEach((node) => {
      careContent.appendChild(node);
    });
    careAccordion.hidden = false;
  }

  function prettifySpecs(source) {
    Array.from(source.children).forEach((node) => {
      if (node.tagName !== 'P') return;
      const value = text(node.textContent);
      const colon = value.indexOf(':');
      if (colon <= 0) return;

      const label = value.slice(0, colon).trim();
      const detail = value.slice(colon + 1).trim();
      if (!detail || label.length > 28 || /^notes?$/i.test(label)) return;

      node.classList.add('tt-spec-row');
      node.innerHTML = `<span class="tt-spec-row__label">${escapeHtml(label)}</span><span>${escapeHtml(detail)}</span>`;
    });
  }

  function tidyEmptyDetails(root) {
    const source = root.querySelector('[data-tt-product-description-source]');
    if (!source) return;
    const hasUsefulContent = text(source.textContent).length > 0 || source.querySelector('img, ul, ol, video, iframe');
    if (!hasUsefulContent) {
      const details = source.closest('.tt-product-accordion');
      if (details) details.hidden = true;
    }
  }

  function parseStructuredSizeGuide(script) {
    if (!script) return null;

    let data;
    try {
      data = JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('ThisThat size guide data could not be parsed.', error);
      return null;
    }

    const entries = Array.isArray(data.entries) ? data.entries.filter((entry) => entry && entry.size) : [];
    if (!entries.length) return null;

    entries.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

    const definitions = [
      ['length', 'Length'],
      ['chest', 'Chest'],
      ['shoulder', 'Shoulder'],
      ['sleeve_length', 'Sleeve length'],
      ['waist', 'Waist'],
      ['hip', 'Hip'],
      ['inseam', 'Inseam'],
      ['outseam', 'Outseam']
    ];

    const formatCm = (value) => {
      const number = Number(value);
      // Empty optional metaobject number fields can arrive as 0. Treat zero (and
      // negative values) as unset so irrelevant measurement rows disappear.
      if (!Number.isFinite(number) || number <= 0) return '';
      return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(2)));
    };

    const formatIn = (value) => {
      const number = Number(value);
      if (!Number.isFinite(number) || number <= 0) return '';
      return (number / 2.54).toFixed(2);
    };

    const sizes = entries.map((entry) => text(entry.size));
    const measurements = definitions.map(([key, label]) => {
      const values = {};
      let populated = false;

      entries.forEach((entry) => {
        const cm = formatCm(entry[key]);
        if (!cm) return;
        populated = true;
        values[text(entry.size)] = { cm, in: formatIn(entry[key]) };
      });

      return populated ? { label, values } : null;
    }).filter(Boolean);

    if (!measurements.length) return null;
    return { sizes, units: ['cm', 'in'], measurements };
  }

  function renderDesktopSizeGuide(container, parsed) {
    const preferredUnits = ['cm', 'in'].filter((unit) => parsed.units.includes(unit));
    const units = preferredUnits.length ? preferredUnits : parsed.units;

    const unitLabel = (unit) => unit === 'cm' ? 'cm' : unit === 'in' ? 'in' : '';

    const head = parsed.sizes.map((size) => `<th scope="col">${escapeHtml(size)}</th>`).join('');
    const rows = parsed.measurements.map(({ label, values }) => {
      const cells = parsed.sizes.map((size) => {
        const sizeValues = values[size] || {};
        const entries = units.length ? units : Object.keys(sizeValues);
        const available = entries.filter((unit) => sizeValues[unit]);

        if (!available.length) return '<td>—</td>';

        const primaryUnit = available.includes('cm') ? 'cm' : available[0];
        const secondaryUnit = available.find((unit) => unit !== primaryUnit);
        const primary = `${escapeHtml(sizeValues[primaryUnit])}${unitLabel(primaryUnit) ? ` ${unitLabel(primaryUnit)}` : ''}`;
        const secondary = secondaryUnit
          ? `<small>${escapeHtml(sizeValues[secondaryUnit])}${unitLabel(secondaryUnit) ? ` ${unitLabel(secondaryUnit)}` : ''}</small>`
          : '';

        return `<td><strong>${primary}</strong>${secondary}</td>`;
      }).join('');

      return `<tr><th scope="row">${escapeHtml(label)}</th>${cells}</tr>`;
    }).join('');

    container.innerHTML = `
      <table class="tt-size-guide__clean-table">
        <thead><tr><th scope="col">Measurement</th>${head}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  function initSizeGuide(root) {
    const modalId = `TTSizeGuide-${root.dataset.section}`;
    const modal = document.getElementById(modalId);
    const guide = modal?.querySelector('[data-tt-size-guide]');
    if (!guide) return;

    const desktop = guide.querySelector('[data-tt-size-guide-desktop]');
    const mobile = guide.querySelector('[data-tt-size-guide-mobile]');
    const fallback = guide.querySelector('[data-tt-size-guide-fallback]');
    const tabs = guide.querySelector('[data-tt-size-tabs]');
    const activeSizeLabel = guide.querySelector('[data-tt-active-size]');
    const measurementList = guide.querySelector('[data-tt-measurements]');
    const unitToggle = guide.querySelector('[data-tt-unit-toggle]');

    const structuredScript = guide.querySelector('[data-tt-structured-size-guide]');
    let parsed = parseStructuredSizeGuide(structuredScript);
    let desktopClone = null;

    if (!parsed) {
      const source = root.querySelector('[data-tt-product-description-source]');
      if (!source) return;

      const table = getLikelySizeTable(source);
      if (!table) return;

      desktopClone = table.cloneNode(true);
      parsed = parseSizeTable(table);
      removeNearbySizeHeading(table);
      table.remove();
    }

    if (!parsed) {
      desktop.hidden = true;
      fallback.hidden = false;
      fallback.innerHTML = '';
      if (desktopClone) fallback.appendChild(desktopClone);
      mobile.hidden = true;
      return;
    }

    renderDesktopSizeGuide(desktop, parsed);
    desktop.hidden = false;
    mobile.hidden = false;
    fallback.hidden = true;

    let selectedSize = parsed.sizes[0];
    let selectedUnit = parsed.units.includes('cm') ? 'cm' : (parsed.units[0] || 'value');

    const sizeFieldsets = Array.from(root.querySelectorAll('variant-selects fieldset'));
    const sizeFieldset = sizeFieldsets.find((fieldset) => {
      const legend = fieldset.querySelector('legend');
      return legend && text(legend.textContent).toLowerCase().includes('size');
    });
    const checkedSize = sizeFieldset?.querySelector('input:checked')?.value;
    if (checkedSize && parsed.sizes.includes(checkedSize)) selectedSize = checkedSize;

    function renderTabs() {
      tabs.innerHTML = parsed.sizes.map((size) => (
        `<button type="button" class="tt-size-guide__size-button${size === selectedSize ? ' is-active' : ''}" data-tt-size="${escapeHtml(size)}">${escapeHtml(size)}</button>`
      )).join('');
    }

    function renderUnits() {
      if (parsed.units.length < 2) {
        unitToggle.hidden = true;
        return;
      }
      unitToggle.hidden = false;
      unitToggle.innerHTML = parsed.units.map((unit) => {
        const label = unit === 'in' ? 'IN' : unit.toUpperCase();
        return `<button type="button" class="tt-size-guide__unit-button${unit === selectedUnit ? ' is-active' : ''}" data-tt-unit="${unit}">${label}</button>`;
      }).join('');
    }

    function renderMeasurements() {
      activeSizeLabel.textContent = selectedSize;
      measurementList.innerHTML = parsed.measurements.map(({ label, values }) => {
        const sizeValues = values[selectedSize] || {};
        let value = sizeValues[selectedUnit];
        let unit = selectedUnit;

        if (!value) {
          const firstEntry = Object.entries(sizeValues)[0];
          value = firstEntry?.[1] || '—';
          unit = firstEntry?.[0] || '';
        }

        const suffix = unit === 'cm' ? ' cm' : unit === 'in' ? ' in' : '';
        return `<div class="tt-size-guide__measurement"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}${suffix}</dd></div>`;
      }).join('');
    }

    function render() {
      renderTabs();
      renderUnits();
      renderMeasurements();
    }

    tabs.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tt-size]');
      if (!button) return;
      selectedSize = button.dataset.ttSize;
      render();
    });

    unitToggle.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tt-unit]');
      if (!button) return;
      selectedUnit = button.dataset.ttUnit;
      render();
    });

    if (sizeFieldset) {
      sizeFieldset.addEventListener('change', (event) => {
        const value = event.target?.value;
        if (parsed.sizes.includes(value)) {
          selectedSize = value;
          render();
        }
      });
    }

    render();
  }

  function moveReviewWidget(root) {
    const slot = root.querySelector('[data-tt-reviews-slot]');
    const section = root.querySelector('[data-tt-reviews-section]');
    if (!slot || !section || slot.children.length) return true;

    const widget = root.querySelector('.jdgm-review-widget, #judgeme_product_reviews');
    if (!widget || slot.contains(widget)) return false;

    const wrapper = widget.closest('.shopify-block') || widget;
    slot.appendChild(wrapper);
    section.hidden = false;
    return true;
  }

  function initReviewRelocation(root) {
    if (moveReviewWidget(root)) return;

    const info = root.querySelector('.product__info-container');
    if (!info) return;

    const observer = new MutationObserver(() => {
      if (moveReviewWidget(root)) observer.disconnect();
    });
    observer.observe(info, { childList: true, subtree: true });

    window.setTimeout(() => observer.disconnect(), 10000);
  }

  function initStickyAddToCart(root) {
    const sticky = root.querySelector('[data-tt-sticky-atc]');
    const stickyButton = root.querySelector('[data-tt-sticky-button]');
    const stickyPrice = root.querySelector('[data-tt-sticky-price]');
    const originalButton = root.querySelector('.product-form__submit');
    const priceArea = root.querySelector('[id^="price-"]');
    if (!sticky || !stickyButton || !originalButton) return;

    let originalVisible = true;

    const sync = () => {
      const originalText = text(originalButton.textContent);
      if (originalText) stickyButton.textContent = originalText;
      stickyButton.disabled = originalButton.disabled || originalButton.getAttribute('aria-disabled') === 'true';
      if (stickyPrice && priceArea) {
        const currentPrice = priceArea.querySelector('.price-item--sale, .price-item--regular');
        if (currentPrice) stickyPrice.textContent = text(currentPrice.textContent);
      }
    };

    const updateVisibility = () => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      // Once the primary purchase button has scrolled away, keep the mobile bar
      // available for the rest of the product page (reviews, recommendations,
      // etc.) instead of hiding it when <product-info> leaves the viewport.
      const show = mobile && !originalVisible;
      sticky.hidden = !show;
      sticky.classList.toggle('is-visible', show);
    };

    stickyButton.addEventListener('click', () => {
      sync();
      if (!stickyButton.disabled) originalButton.click();
    });

    const buttonObserver = new IntersectionObserver((entries) => {
      originalVisible = entries[0]?.isIntersecting ?? true;
      updateVisibility();
    }, { threshold: .15 });

    buttonObserver.observe(originalButton);

    const mutationObserver = new MutationObserver(sync);
    mutationObserver.observe(originalButton, { attributes: true, childList: true, subtree: true });
    if (priceArea) mutationObserver.observe(priceArea, { childList: true, subtree: true });

    window.addEventListener('resize', updateVisibility, { passive: true });
    sync();
    updateVisibility();
  }

  const styleSectionCache = window.__TT_STYLE_SECTION_CACHE__ || new Map();
  window.__TT_STYLE_SECTION_CACHE__ = styleSectionCache;

  function preservePreviewParams(targetUrl) {
    const currentUrl = new URL(window.location.href);
    ['preview_theme_id', 'preview_script_debug', '_fd', 'pb'].forEach((key) => {
      if (!targetUrl.searchParams.has(key) && currentUrl.searchParams.has(key)) {
        targetUrl.searchParams.set(key, currentUrl.searchParams.get(key));
      }
    });
    return targetUrl;
  }

  function styleDisplayUrl(href) {
    return preservePreviewParams(new URL(href, window.location.href));
  }

  function styleSectionUrl(href, sectionId) {
    const url = styleDisplayUrl(href);
    url.searchParams.set('section_id', sectionId);
    return url;
  }

  async function fetchStyleSection(href, sectionId) {
    const requestUrl = styleSectionUrl(href, sectionId);
    const key = requestUrl.toString();
    if (styleSectionCache.has(key)) return styleSectionCache.get(key);

    const promise = fetch(requestUrl.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Colour switch failed (${response.status})`);
      const responseText = await response.text();
      if (!responseText || responseText.trim() === 'null') throw new Error('Colour section was not available');
      return responseText;
    }).catch((error) => {
      styleSectionCache.delete(key);
      throw error;
    });

    styleSectionCache.set(key, promise);
    return promise;
  }

  function updateDocumentTitle(newRoot) {
    const productTitle = newRoot.dataset.ttProductTitle;
    if (!productTitle) return;

    const current = document.title || '';
    const separator = current.includes(' – ') ? ' – ' : current.includes(' | ') ? ' | ' : null;
    if (!separator) return;
    const suffix = current.split(separator).slice(1).join(separator).trim();
    if (suffix) document.title = `${productTitle}${separator}${suffix}`;
  }

  function refreshExternalRecommendations(productId, newRoot) {
    if (!productId) return;
    document.querySelectorAll('product-recommendations').forEach((recommendations) => {
      if (newRoot.contains(recommendations)) return;
      recommendations.dataset.productId = productId;
      recommendations.innerHTML = '';
      recommendations.classList.remove('product-recommendations--loaded');
      if (typeof recommendations.initializeRecommendations === 'function') {
        recommendations.initializeRecommendations(productId);
      }
    });
  }

  function prefetchStyleSiblings(root) {
    if (!root?.classList.contains('tt-product-v3') || root.dataset.ttAjaxColours !== 'true') return;
    const sectionId = root.dataset.section;
    const links = Array.from(root.querySelectorAll('[data-tt-style-colour-link]'))
      .filter((link) => !link.classList.contains('is-active'))
      .slice(0, 8);

    const prefetch = () => links.forEach((link) => {
      fetchStyleSection(hrefForSelectedSize(root, link), sectionId).catch(() => {});
    });

    if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 1300 });
    else window.setTimeout(prefetch, 550);
  }

  async function swapStyleProduct(root, href, { historyMode = 'push', focusActive = false } = {}) {
    if (!root || root.dataset.ttAjaxColours !== 'true') {
      window.location.href = href;
      return;
    }

    const sectionId = root.dataset.section;
    const targetUrl = styleDisplayUrl(href);
    const targetPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;

    if (targetUrl.pathname === new URL(root.dataset.url, window.location.origin).pathname) return;

    root.classList.add('is-tt-style-loading');
    root.setAttribute('aria-busy', 'true');

    try {
      const responseText = await fetchStyleSection(targetUrl.toString(), sectionId);
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');
      const newRoot = parsed.querySelector('product-info.tt-product-v2');
      if (!newRoot) throw new Error('Product section markup was not found');

      // External scripts are already loaded on the page. Keeping them inside a
      // cross-product swap risks duplicate event listeners; JSON data scripts
      // remain because the size guide and product media use them as data.
      newRoot.querySelectorAll('script[src]').forEach((script) => script.remove());

      const y = window.scrollY;
      root.replaceWith(newRoot);
      window.scrollTo(0, y);

      if (historyMode === 'push') window.history.pushState({ ttProductStyle: true }, '', targetPath);
      else if (historyMode === 'replace') window.history.replaceState({ ttProductStyle: true }, '', targetPath);

      updateDocumentTitle(newRoot);
      refreshExternalRecommendations(newRoot.dataset.productId, newRoot);

      // Judge.me normally observes page mutations, but explicitly asking it to
      // initialise keeps reviews reliable after an AJAX product swap.
      window.setTimeout(() => window.jdgm?.initWidgets?.(), 0);

      if (focusActive) {
        window.setTimeout(() => newRoot.querySelector('[data-tt-style-colour-link].is-active')?.focus(), 0);
      }

      prefetchStyleSiblings(newRoot);
    } catch (error) {
      console.warn('[ThisThat colour switch] Falling back to normal navigation.', error);
      window.location.href = targetUrl.toString();
    } finally {
      root.classList.remove('is-tt-style-loading');
      root.removeAttribute('aria-busy');
    }
  }

  function getCurrentSelectedSize(root) {
    const fieldsets = Array.from(root.querySelectorAll('variant-selects fieldset'));
    const sizeFieldset = fieldsets.find((fieldset) => {
      const legend = fieldset.querySelector('legend');
      return legend && text(legend.textContent).toLowerCase().includes('size');
    });
    return sizeFieldset?.querySelector('input:checked')?.value || '';
  }

  function hrefForSelectedSize(root, link) {
    const size = getCurrentSelectedSize(root);
    if (!size) return link.href;
    const match = Array.from(link.querySelectorAll('[data-tt-colour-variant]'))
      .find((node) => node.dataset.size === size);
    return match?.dataset.url || link.href;
  }

  function bindStyleSwitching() {
    if (window.__TT_STYLE_SWITCH_V32__) return;
    window.__TT_STYLE_SWITCH_V32__ = true;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-tt-style-colour-link]');
      if (!link || link.classList.contains('is-active')) return;

      const root = link.closest(ROOT_SELECTOR);
      if (!root || root.dataset.ttAjaxColours !== 'true') return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      link.classList.add('is-loading');
      const targetHref = hrefForSelectedSize(root, link);
      swapStyleProduct(root, targetHref, { historyMode: 'push', focusActive: event.detail === 0 });
    });

    document.addEventListener('pointerenter', (event) => {
      const link = event.target.closest?.('[data-tt-style-colour-link]');
      const root = link?.closest(ROOT_SELECTOR);
      if (!link || !root || root.dataset.ttAjaxColours !== 'true' || link.classList.contains('is-active')) return;
      fetchStyleSection(hrefForSelectedSize(root, link), root.dataset.section).catch(() => {});
    }, true);

    document.addEventListener('change', (event) => {
      const root = event.target.closest?.(`${ROOT_SELECTOR}.tt-product-v3`);
      if (!root || root.dataset.ttAjaxColours !== 'true') return;
      const fieldset = event.target.closest('fieldset');
      const legend = fieldset?.querySelector('legend');
      if (!legend || !text(legend.textContent).toLowerCase().includes('size')) return;
      prefetchStyleSiblings(root);
    });

    window.addEventListener('popstate', () => {
      const root = document.querySelector(`${ROOT_SELECTOR}.tt-product-v3`);
      if (!root || root.dataset.ttAjaxColours !== 'true') return;
      const currentPath = new URL(root.dataset.url, window.location.origin).pathname;
      if (currentPath === window.location.pathname) return;
      swapStyleProduct(root, window.location.href, { historyMode: 'none' });
    });
  }

  function initProductV2(root) {
    if (!root || root.dataset.ttProductV2Ready === 'true') return;
    root.dataset.ttProductV2Ready = 'true';

    initSizeGuide(root);

    const source = root.querySelector('[data-tt-product-description-source]');
    if (source) {
      extractCare(source, root);
      prettifySpecs(source);
      tidyEmptyDetails(root);
    }

    initReviewRelocation(root);
    initStickyAddToCart(root);
    prefetchStyleSiblings(root);
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initProductV2(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initProductV2);
  }

  bindStyleSwitching();

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('product-info:loaded', (event) => initAll(event.target));
})();
